import { HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  BaselineResponse,
  BulkImportStatus,
  BulkImportType,
  DAResponse,
  GraphDataAPIPoint,
  GraphDataAPIResponse,
  GraphDataPoint,
  GraphDataResponse,
  GraphDimension,
  GraphTimeSeriesData,
  OfferResponse,
  ProgramResponse,
  RegistrationResponse,
  ScheduleResponse
} from '@dr-customer-offers-ui/lib-interfaces';
import {
  BaselineService,
  ComBulkImportService,
  DeclaredAvailabilityService,
  OfferService,
  ProgramService,
  RegistrationService,
  RegistrationViewConfigService,
  ScheduleService
} from '@dr-customer-offers-ui/lib-services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DataModel, NgxIntervalDataGridService, OperationalGroup, OperationalHours, TableDataTypes } from '@ngx-interval-data-grid';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NgxDropdownOption } from 'ngx-dropdown';
import { combineLatest, EMPTY, forkJoin, fromEvent, of, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  first,
  map,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators';
import {
  BULK_IMPORT_POLL_INITIAL_DELAY_MS,
  BULK_IMPORT_POLL_INTERVAL_MS,
  BULK_IMPORT_POLL_TIMEOUT_MS,
  BULK_IMPORT_TERMINAL_STATUSES
} from '../../shared/constants/bulk-import.constants';
import { DayOfWeekModel, RegConfig, transformRegConfig } from '../../shared/models';
import { DaterangeISO } from '../../shared/models/daterange';
import { RoleTypeEnum, UserRolesEventDetail } from '../../shared/models/role';
import { User, UserEventDetail, UserResource } from '../../shared/models/user.model';
import { BulkImportNotificationService } from '../../shared/services/bulk-import-notification.service';
import { BulkUploadFileService } from '../../shared/services/bulk-upload-file.service';
import { DataModelService } from '../../shared/services/data-model.service';
import { DataViewModelService } from '../../shared/services/data-vm.service';
import { DateService } from '../../shared/services/date.service';
import { InternalService } from '../../shared/services/internal.service';
import { MessageService } from '../../shared/services/message.service';
import * as COMActions from '../actions/com.actions';
import * as COMSelectors from '../selectors/com.selectors';
import { selectUser } from '../selectors/com.selectors';

@Injectable()
export class COMEffects {
  private readonly DIMENSION_MAP: Record<string, { name: string; valueKey: string; nullFallback: number | null }> = {
    DEMAND_FORECAST: { name: 'Demand', valueKey: 'DEMAND_FORECAST', nullFallback: 0 },
    PRICE: { name: 'Price', valueKey: 'PRICE', nullFallback: 0 },
    kW: { name: 'Capacity', valueKey: 'kW', nullFallback: 0 },
    KW_TO_INCREASE: { name: 'Capacity Increase', valueKey: 'KW_TO_INCREASE', nullFallback: null },
    KW_TO_DECREASE: { name: 'Capacity Decrease', valueKey: 'KW_TO_DECREASE', nullFallback: null },
    PRICE_TO_INCREASE: { name: 'Price Increase', valueKey: 'PRICE_TO_INCREASE', nullFallback: null },
    PRICE_TO_DECREASE: { name: 'Price Decrease', valueKey: 'PRICE_TO_DECREASE', nullFallback: null }
  };
  constructor(
    private actions$: Actions,
    private programService: ProgramService,
    private internalService: InternalService,
    private store: Store,
    private registrationService: RegistrationService,
    private registrationViewConfigService: RegistrationViewConfigService,
    private offerService: OfferService,
    private scheduleService: ScheduleService,
    private ngxService: NgxIntervalDataGridService,
    private dataModelService: DataModelService,
    private dataVMService: DataViewModelService,
    private baselineService: BaselineService,
    private daService: DeclaredAvailabilityService,
    private messageService: MessageService,
    private dateService: DateService,
    private bulkImportService: ComBulkImportService,
    private bulkUploadFileService: BulkUploadFileService,
    private bulkImportNotificationService: BulkImportNotificationService,
    private translate: TranslateService,
    private router: Router
  ) {}

  listenUserFromWindow$ = createEffect(
    () =>
      // Optional:
      // this.zone.runOutsideAngular(() =>
      fromEvent<CustomEvent<UserEventDetail>>(window, 'user').pipe(
        tap((evt) => console.log('[window user event]', evt)),
        map((evt) => evt.detail?.user),
        filter((u): u is User => !!u),

        // Optional: avoid dispatching same user again and again (adjust key as needed)
        distinctUntilChanged((a, b) => a.userId === b.userId),

        map((user) => COMActions.userReceived({ user }))
      )
    // )
  );

  listenUserRolesFromWindow$ = createEffect(() =>
    fromEvent<CustomEvent<UserRolesEventDetail>>(window, 'userRoles').pipe(
      tap((evt) => console.log('[window userRoles event]', evt)),
      map((evt) => evt.detail?.userRoles ?? []),
      map((roles) => COMActions.rolesReceived({ roles }))
    )
  );

  loadPrograms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadPrograms),
      switchMap(() =>
        this.programService.getPrograms().pipe(
          map((programs: ProgramResponse[]) => {
            const userPreference = this.internalService.userPreferenceSignal();
            const apiLocale = userPreference?.apiLocale ?? 'en_US';

            // Map programs to dropdown options
            const programsData: NgxDropdownOption[] = programs?.length
              ? programs.map((program: ProgramResponse) => ({
                  value: this.getLocalisedLabel(program.display_labels, apiLocale, 'en_US'),
                  id: program.id
                }))
              : [];

            // Sort programs alphabetically
            const sortedPrograms = programsData.sort((a, b) => a.value.localeCompare(b.value));

            // Use this for testing particular program
            // const desiredProgram = programsData?.filter((program) => program.value.indexOf('Amandak') >= 0)[0];
            // const selectedProgram = desiredProgram || sortedProgsData[0];
            const selectedProgram = sortedPrograms[0] || null;

            return COMActions.loadProgramsSuccess({
              programs: sortedPrograms,
              selectedProgram
            });
          }),
          catchError((error) => {
            console.error('Error Loading Programs', error);
            return of(
              COMActions.loadProgramsFailure({
                error: error?.message || 'Failed to load programs'
              })
            );
          })
        )
      )
    )
  );

  updateInternalServiceOnProgramLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(COMActions.loadProgramsSuccess),
        tap(({ selectedProgram }) => {
          if (selectedProgram) {
            this.internalService.setSelectedProgram(selectedProgram);
          }
        })
      ),
    { dispatch: false }
  );

  // Effect to handle program selection
  selectProgram$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(COMActions.selectProgram),
        tap(({ program }) => {
          this.internalService.setSelectedProgram(program);
          // Trigger registrations load when program changes
          this.store.dispatch(COMActions.loadRegistrations({ programId: program.id }));
        })
      ),
    { dispatch: false }
  );

  // Load registrations by program ID
  loadRegistrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadRegistrations),
      switchMap(({ programId }) =>
        this.registrationService.getRegistrations(programId).pipe(
          map((registrations: RegistrationResponse[]) => {
            const userPreference = this.internalService.userPreferenceSignal();
            const apiLocale = userPreference?.apiLocale ?? 'en_US';

            // Check if registrations are empty
            if (!registrations || !registrations.length) {
              return COMActions.loadRegistrationsSuccess({
                registrations: [],
                selectedRegistration: null
              });
            }

            // Map registrations to dropdown options
            const registrationsData: NgxDropdownOption[] = registrations.map((registration: RegistrationResponse) => ({
              value: this.getLocalisedLabel(registration.display_labels, apiLocale, 'en_US'),
              id: registration.id
            }));

            // Sort registrations alphabetically
            const sortedRegistrations = registrationsData.sort((a, b) => a.value.localeCompare(b.value));

            // Select first registration by default
            const selectedRegistration = sortedRegistrations[0] || null;

            // Update internal service
            if (selectedRegistration) {
              this.internalService.setSelectedReg(selectedRegistration);
            }

            return COMActions.loadRegistrationsSuccess({
              registrations: sortedRegistrations,
              selectedRegistration
            });
          }),
          catchError((error) => {
            console.error('Error Loading Registrations', error);
            return of(
              COMActions.loadRegistrationsFailure({
                error: error?.message || 'Failed to load registrations'
              })
            );
          })
        )
      )
    )
  );

  // Load registrations using session token
  loadRegistrationsUsingSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadRegistrationsUsingSession),
      switchMap(() =>
        this.registrationService.getRegistrationsUsingSession().pipe(
          // Cancel this effect if cancelLoadRegistrations or loadRegistrations is dispatched
          takeUntil(this.actions$.pipe(ofType(COMActions.cancelLoadRegistrations, COMActions.loadRegistrations))),
          map((registrations: RegistrationResponse[]) => {
            const userPreference = this.internalService.userPreferenceSignal();
            const apiLocale = userPreference?.apiLocale ?? 'en_US';

            // Check if registrations are empty
            if (!registrations || !registrations.length) {
              return COMActions.loadRegistrationsSuccess({
                registrations: [],
                selectedRegistration: null
              });
            }

            // Map registrations to dropdown options
            const registrationsData: NgxDropdownOption[] = registrations.map((registration: RegistrationResponse) => ({
              value: this.getLocalisedLabel(registration.display_labels, apiLocale, 'en_US'),
              id: registration.id
            }));

            // Sort registrations alphabetically
            const sortedRegistrations = registrationsData.sort((a, b) => a.value.localeCompare(b.value));

            // Select first registration by default
            const selectedRegistration = sortedRegistrations[0] || null;

            // Update internal service
            if (selectedRegistration) {
              this.internalService.setSelectedReg(selectedRegistration);
            }

            return COMActions.loadRegistrationsSuccess({
              registrations: sortedRegistrations,
              selectedRegistration
            });
          }),
          catchError((error) => {
            // Only log error if it's not a cancellation
            if (error.name !== 'EmptyError') {
              console.error('Error Loading Registrations Using Session', error);
            }
            return of(
              COMActions.loadRegistrationsFailure({
                error: error?.message || 'Failed to load registrations'
              })
            );
          })
        )
      )
    )
  );

  // Load RegViewConfig with caching logic
  loadRegViewConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadRegViewConfig),
      withLatestFrom(this.store.select(COMSelectors.selectRegViewConfigCache)),
      switchMap(([{ registrationId }, cache]) => {
        // Check if already in cache
        if (cache[registrationId]) {
          console.log(`RegViewConfig for ${registrationId} found in cache`);
          return of(
            COMActions.loadRegViewConfigSuccess({
              registrationId,
              regViewConfig: cache[registrationId].config
            })
          );
        }

        const user$ = this.store.select(selectUser).pipe(
          filter((u): u is User => !!u),
          take(1)
        );

        // Not in cache, fetch from API
        console.log(`Fetching RegViewConfig for ${registrationId} from API`);
        return combineLatest([this.registrationViewConfigService.getRegistrationViewConfig(registrationId), user$]).pipe(
          map(([regConfigResponse, user]) => {
            if (!regConfigResponse) {
              return COMActions.loadRegViewConfigFailure({
                registrationId,
                error: 'No configuration found'
              });
            }

            let ignoreOfferRules = false;

            if (regConfigResponse && !regConfigResponse.override_ignore_gate_rules) {
              ignoreOfferRules = !!user?.resources.find((resource: UserResource) => resource.resourceName === RoleTypeEnum.COM_IGNORE_OFFER_RULES);
            }

            // Apply business logic
            if (ignoreOfferRules) {
              regConfigResponse.gate_rules.post_gate_edits = {
                ...regConfigResponse.gate_rules.post_gate_edits,
                allow_offer_edit: true,
                allow_post_gate_closure_edits: true
              };
            }

            // Set grouped offers signal
            const isGroupedOffers = regConfigResponse.operational_hours?.length ? true : false;
            this.internalService.isGroupedOffersSignal?.set(isGroupedOffers);

            // Transform the config
            const userPreference = this.internalService.userPreferenceSignal();
            const transformedConfig: RegConfig = transformRegConfig(regConfigResponse, userPreference?.apiLocale ?? 'en_US');

            // Update internal service
            this.internalService.setRegViewConfigState(transformedConfig);

            return COMActions.loadRegViewConfigSuccess({
              registrationId,
              regViewConfig: transformedConfig
            });
          }),
          catchError((error) => {
            console.error('Error Loading RegViewConfig', error);
            return of(
              COMActions.loadRegViewConfigFailure({
                registrationId,
                error: error?.message || 'Failed to load registration view config'
              })
            );
          })
        );
      })
    )
  );

  // Load Graph Offers
  loadGraphOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadGraphOffers),
      switchMap(({ currentDate }) =>
        this.store.select(COMSelectors.selectCurrentRegViewConfig).pipe(
          first(),
          switchMap((regViewConfig) => {
            // clause if no registration is selected
            if (!regViewConfig) {
              console.warn('No registration ID available for loading graph offers');
              return of(
                COMActions.loadGraphOffersFailure({
                  error: 'No registration selected'
                })
              );
            }

            const userTimeZone = regViewConfig.timeZone || 'UTC';

            // Parse as if it were in the reg timezone
            const startOfDayInUserTz = moment.tz(currentDate, userTimeZone).startOf('day');
            const endOfDayInUserTz = moment.tz(currentDate, userTimeZone).endOf('day');

            // Format as UTC strings
            const startDate = startOfDayInUserTz.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
            const endDate = endOfDayInUserTz.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

            // graph_view && display_baselines_tab -> overlay baseline as a line
            const wantsBaseline = !!regViewConfig.graph_view && !!regViewConfig.tab_display?.display_baselines_tab;

            const graph$ = this.offerService.getGraphDataOffers(regViewConfig.registrationId, startDate, endDate);

            const baseline$ = wantsBaseline
              ? this.baselineService.getBaselines(regViewConfig.registrationId, startDate, endDate).pipe(
                  catchError((error) => {
                    console.error('Error loading baseline for graph overlay', error);
                    return of([] as BaselineResponse[]); // don't fail the whole graph if baseline dies
                  })
                )
              : of([] as BaselineResponse[]);

            return forkJoin({ graph: graph$, baseline: baseline$ }).pipe(
              map(({ graph, baseline }) => {
                const graphOffers = this.transformApiResponseToGraphDataResponse(graph, regViewConfig);
                const merged = wantsBaseline ? this.mergeBaselineIntoGraph(graphOffers, baseline, startDate, endDate) : graphOffers;
                return COMActions.loadGraphOffersSuccess({ graphOffers: merged });
              }),
              catchError((error) => {
                console.error('Error Loading Graph Offers', error);
                return of(COMActions.loadGraphOffersFailure({ error: error?.message || 'Failed to load graph offers' }));
              })
            );
          })
        )
      )
    )
  );

  // Load Cleared Graph Offers
  loadClearedGraphOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadClearedGraphOffers),
      withLatestFrom(this.store.select(COMSelectors.selectCurrentRegViewConfig)),
      switchMap(([{ currentDate }, regViewConfig]) => {
        if (!regViewConfig) {
          console.warn('No registration ID available for loading cleared graph offers');
          return of(
            COMActions.loadClearedGraphOffersFailure({
              error: 'No registration selected'
            })
          );
        }
        const userTimeZone = regViewConfig.timeZone || 'UTC';
        const dateString = moment.utc(currentDate).format('YYYY-MM-DD');

        const startOfDayInUserTz = moment.tz(dateString, userTimeZone).startOf('day');
        const endOfDayInUserTz = moment.tz(dateString, userTimeZone).endOf('day');

        // Format as UTC strings
        const startDate = startOfDayInUserTz.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
        const endDate = endOfDayInUserTz.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

        return this.offerService.getClearedGraphDataOffers(regViewConfig.registrationId, startDate, endDate).pipe(
          map((apiResponse: GraphDataAPIResponse) => {
            const graphOffers = this.transformApiResponseToGraphDataResponse(apiResponse, regViewConfig);

            return COMActions.loadClearedGraphOffersSuccess({
              graphOffers
            });
          }),
          catchError((error) => {
            console.error('Error Loading Cleared Graph Offers', error);
            return of(
              COMActions.loadClearedGraphOffersFailure({
                error: error?.message || 'Failed to load cleared graph offers'
              })
            );
          })
        );
      })
    )
  );

  // Load Open Offers
  loadOpenOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadOpenOffers),
      switchMap(({ registrationId, startDate, endDate }) =>
        this.store.select(COMSelectors.selectCurrentRegViewConfig).pipe(
          first((regConfig) => regConfig !== null && regConfig !== undefined),
          switchMap((regConfig) => {
            if (!regConfig) {
              return of(
                COMActions.loadOpenOffersFailure({
                  error: 'No registration config available'
                })
              );
            }

            const hasOperationalHours = regConfig.operational_hours && regConfig.operational_hours.length > 0;

            if (hasOperationalHours) {
              // Load grouped offers
              return this.offerService.getGroupedOffers(registrationId, startDate, endDate).pipe(
                map((groups: OperationalGroup[] | null) => {
                  const groupedData = this.ngxService.groupByIntervals(
                    groups as OperationalGroup[],
                    regConfig.operational_hours as OperationalHours[],
                    TableDataTypes.OPEN
                  );

                  const selectedDates: DaterangeISO = {
                    start: startDate,
                    end: endDate
                  };

                  return COMActions.loadOpenOffersSuccess({
                    offers: {
                      values: groupedData,
                      selectedDateRange: selectedDates
                    }
                  });
                }),
                catchError((error) => {
                  console.error('Error Loading Grouped Offers', error);
                  return of(
                    COMActions.loadOpenOffersFailure({
                      error: error?.message || 'Failed to load grouped offers'
                    })
                  );
                })
              );
            } else {
              return this.offerService.getOffers(registrationId, startDate, endDate).pipe(
                map((offers: OfferResponse[] | null) => {
                  const offerData: DataModel[] = this.dataModelService.getGridDataFromOffers(offers as OfferResponse[], regConfig);

                  const dayOfWeek: DayOfWeekModel = {
                    data: offerData,
                    timeZone: regConfig.timeZone,
                    startDate: moment.utc(startDate),
                    endDate: moment.utc(endDate),
                    intervalFrequency: regConfig.intervalFrequency,
                    tableType: TableDataTypes.OPEN
                  };

                  const groupedData = this.ngxService.groupByDayOfWeek(dayOfWeek.data, dayOfWeek.timeZone, dayOfWeek.tableType);

                  const selectedDates: DaterangeISO = {
                    start: startDate,
                    end: endDate
                  };

                  return COMActions.loadOpenOffersSuccess({
                    offers: {
                      values: groupedData,
                      selectedDateRange: selectedDates
                    }
                  });
                }),
                catchError((error) => {
                  console.error('Error Loading Offers', error);
                  return of(
                    COMActions.loadOpenOffersFailure({
                      error: error?.message || 'Failed to load offers'
                    })
                  );
                })
              );
            }
          })
        )
      )
    )
  );

  loadClearedOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadClearedOffers),
      withLatestFrom(this.store.select(COMSelectors.selectCurrentRegViewConfig)),
      switchMap(([{ registrationId, startDate, endDate }, regConfig]) => {
        if (!regConfig) {
          return of(
            COMActions.loadClearedOffersFailure({
              error: 'No registration config available'
            })
          );
        }

        console.log(`Fetching cleared offers for ${registrationId}`);

        return this.offerService.getOffers(registrationId, startDate, endDate).pipe(
          map((offers: OfferResponse[] | null) => {
            const offerData: DataModel[] = this.dataModelService.getGridDataFromOffers(offers as OfferResponse[], regConfig);

            const dayOfWeek: DayOfWeekModel = {
              data: offerData,
              timeZone: regConfig.timeZone,
              startDate: moment.utc(startDate),
              endDate: moment.utc(endDate),
              intervalFrequency: regConfig.intervalFrequency,
              tableType: TableDataTypes.CLEAR
            };

            const groupedData = this.ngxService.groupByDayOfWeek(dayOfWeek.data, dayOfWeek.timeZone, dayOfWeek.tableType);

            const selectedDates: DaterangeISO = {
              start: startDate,
              end: endDate
            };

            return COMActions.loadClearedOffersSuccess({
              offers: {
                values: groupedData,
                selectedDateRange: selectedDates
              }
            });
          }),
          catchError((error) => {
            console.error('Error Loading Cleared Offers', error);
            return of(
              COMActions.loadClearedOffersFailure({
                error: error?.message || 'Failed to load cleared offers'
              })
            );
          })
        );
      })
    )
  );

  // Schedule Offers Effects can be added here similarly
  loadScheduledOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadScheduledOffers),
      withLatestFrom(this.store.select(COMSelectors.selectCurrentRegViewConfig)),
      switchMap(([_, regConfig]) => {
        if (!regConfig) {
          return of(
            COMActions.loadScheduledOffersFailure({
              error: 'No registration config available'
            })
          );
        }

        return this.scheduleService.getSchedule(regConfig.registrationId).pipe(
          map((offers: ScheduleResponse[]) => {
            this.ngxService.clearUnSavedData();
            const selectedDates = this.dataVMService.getAppliedWeekRange();
            const selectedDatesISO = selectedDates ? this.dateService.getISODate(selectedDates, regConfig.timeZone) : undefined;
            const scheduleData: DataModel[] = this.dataModelService.getGridDataFromSchedule(offers, regConfig);
            //TODO - remove this
            const scheduleFirstIndex: DataModel = scheduleData[0];
            const scheduleLastIndex: DataModel = scheduleData[1];

            const startdate: moment.Moment = moment(scheduleFirstIndex.offer_start_dttm_utc);
            const enddate: moment.Moment = moment(scheduleLastIndex.offer_end_dttm_utc);

            const dayOfWeek: DayOfWeekModel = {
              data: scheduleData,
              timeZone: regConfig.timeZone,
              startDate: startdate.utc(),
              endDate: enddate.utc(),
              intervalFrequency: regConfig.intervalFrequency,
              tableType: TableDataTypes.SCHEDULE
            };

            const groupedData = this.ngxService.groupByDayOfWeek(dayOfWeek.data, dayOfWeek.timeZone, dayOfWeek.tableType);

            this.internalService.scheduleDataSignal.set({
              values: groupedData,
              regConfig: regConfig,
              selectedDateRange: selectedDates ?? undefined
            });

            // const selectedDatesISO: DaterangeISO = {
            //   start: startDate,
            //   end: endDate
            // };
            return COMActions.loadScheduledOffersSuccess({
              offers: {
                values: groupedData,
                selectedDateRange: selectedDatesISO
              }
            });
          }),
          catchError((error) => {
            console.error('Error Loading Scheduled Offers', error);
            return of(
              COMActions.loadScheduledOffersFailure({
                error: error?.message || 'Failed to load scheduled offers'
              })
            );
          })
        );
      })
    )
  );

  // Baseline Offers Effects
  loadBaselineOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadBaselineOffers),
      withLatestFrom(this.store.select(COMSelectors.selectCurrentRegViewConfig)),
      switchMap(([_, regConfig]) => {
        if (!regConfig) {
          return of(
            COMActions.loadBaselineOffersFailure({
              error: 'No registration config available'
            })
          );
        }

        const selectedDates = this.dataVMService.getAppliedWeekRange();
        if (!selectedDates) {
          return of(COMActions.loadBaselineOffersFailure({ error: 'No date range selected' }));
        }
        const selectedDatesISO = this.dateService.getISODate(selectedDates, regConfig.timeZone);

        return this.baselineService.getBaselines(regConfig.registrationId, selectedDatesISO.start, selectedDatesISO.end).pipe(
          map((offers: BaselineResponse[]) => {
            this.ngxService.clearUnSavedData();
            const baselineData: DataModel[] = this.dataModelService.getGridDataFromBaseline(offers, regConfig);

            const baselineFirstIndex: DataModel = baselineData[0];
            const baselineLastIndex: DataModel = baselineData[1];

            const startdate: moment.Moment = moment(baselineFirstIndex.offer_start_dttm_utc);
            const enddate: moment.Moment = moment(baselineLastIndex.offer_end_dttm_utc);

            const dayOfWeek: DayOfWeekModel = {
              data: baselineData,
              timeZone: regConfig.timeZone,
              startDate: startdate.utc(),
              endDate: enddate.utc(),
              intervalFrequency: regConfig.intervalFrequency,
              tableType: TableDataTypes.BASELINE
            };

            const groupedData = this.ngxService.groupByDayOfWeek(dayOfWeek.data, dayOfWeek.timeZone, dayOfWeek.tableType);

            return COMActions.loadBaselineOffersSuccess({
              offers: {
                values: groupedData
              }
            });
          }),
          catchError((error) => {
            console.error('Error Loading Baseline Offers', error);
            return of(
              COMActions.loadBaselineOffersFailure({
                error: error?.message || 'Failed to load baseline offers'
              })
            );
          })
        );
      })
    )
  );

  // DA Offers Effects
  loadDAOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.loadDAOffers),
      withLatestFrom(this.store.select(COMSelectors.selectCurrentRegViewConfig)),
      switchMap(([{ registrationId, startDate, endDate }, regConfig]) => {
        if (!regConfig) {
          return of(
            COMActions.loadDAOffersFailure({
              error: 'No registration config available'
            })
          );
        }

        return this.daService.getDA(registrationId, startDate, endDate).pipe(
          map((offers: DAResponse[] | null) => {
            this.ngxService.clearUnSavedData();
            const daData: DataModel[] = this.dataModelService.getGridDataFromDA(offers as DAResponse[], regConfig);

            const dayOfWeek: DayOfWeekModel = {
              data: daData,
              timeZone: regConfig.timeZone,
              startDate: moment.utc(startDate),
              endDate: moment.utc(endDate),
              intervalFrequency: regConfig.intervalFrequency,
              tableType: TableDataTypes.DECLARED_AVAILABILITIES
            };

            const groupedData = this.ngxService.groupByDayOfWeek(dayOfWeek.data, dayOfWeek.timeZone, dayOfWeek.tableType);

            return COMActions.loadDAOffersSuccess({
              offers: {
                values: groupedData
              }
            });
          }),
          catchError((error) => {
            console.error('Error Loading DA Offers', error);
            return of(
              COMActions.loadDAOffersFailure({
                error: error?.message || 'Failed to load DA offers'
              })
            );
          })
        );
      })
    )
  );

  // DA Offers POST Effects can be added here
  postDAOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.postDAOffers),
      switchMap(({ offers, registrationId, startDate, endDate }) =>
        this.daService.putDAOffers(registrationId, offers).pipe(
          map(() => {
            this.messageService.handleSuccess(this.translate.instant('COM.TABS.OPEN.CHART.SUCCESS_TEXT'));
            return COMActions.loadDAOffers({ registrationId, startDate, endDate });
          }),
          catchError((error) => {
            console.error('Error Posting DA Offers', error);
            return of(
              COMActions.postDAOffersFailure({
                error: error?.message || 'Failed to post DA offers'
              })
            );
          })
        )
      )
    )
  );

  // Baseline Offers POST Effects can be added here
  postBaselineOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.postBaselineOffers),
      withLatestFrom(this.store.select(COMSelectors.selectCurrentRegViewConfig)),
      switchMap(([{ offers }, regViewConfig]) => {
        if (!regViewConfig) {
          return of(
            COMActions.postBaselineOffersFailure({
              error: 'No registration config available'
            })
          );
        }

        return this.baselineService.putBaselines(regViewConfig.registrationId, offers).pipe(
          map(() => {
            this.messageService.handleSuccess(this.translate.instant('COM.TABS.BASELINES.SUCCESS_TEXT'));
            return COMActions.loadBaselineOffers();
          }),
          catchError((error) => {
            console.error('Error Posting Baseline Offers', error);
            return of(
              COMActions.postBaselineOffersFailure({
                error: error?.message || 'Failed to post baseline offers'
              })
            );
          })
        );
      })
    )
  );

  // Schedule Offers POST Effects can be added here
  postScheduledOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.postScheduledOffers),
      withLatestFrom(this.store.select(COMSelectors.selectCurrentRegViewConfig)),
      switchMap(([{ offers }, regViewConfig]) => {
        if (!regViewConfig) {
          return of(
            COMActions.postScheduledOffersFailure({
              error: 'No registration config available'
            })
          );
        }

        return this.scheduleService.postSchedule(regViewConfig.registrationId, offers).pipe(
          map(() => {
            this.messageService.handleSuccess(this.translate.instant('COM.TABS.SCHEDULE.SUCCESS_TEXT'));
            return COMActions.loadScheduledOffers();
          }),
          catchError((error) => {
            console.error('Error Posting Scheduled Offers', error);
            return of(
              COMActions.postScheduledOffersFailure({
                error: error?.message || 'Failed to post scheduled offers'
              })
            );
          })
        );
      })
    )
  );

  // Schedule Offers PUT Effects can be added here
  putScheduledOffers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.putScheduledOffers),
      withLatestFrom(this.store.select(COMSelectors.selectCurrentRegViewConfig)),
      switchMap(([{ offers }, regViewConfig]) => {
        if (!regViewConfig) {
          return of(
            COMActions.putScheduledOffersFailure({
              error: 'No registration config available'
            })
          );
        }
        return this.scheduleService.putSchedule(regViewConfig.registrationId, offers).pipe(
          map(() => {
            this.messageService.handleSuccess(this.translate.instant('COM.TABS.SCHEDULE.SUCCESS_TEXT'));
            return COMActions.loadScheduledOffers();
          }),
          catchError((error) => {
            console.error('Error Updating Scheduled Offers', error);
            return of(
              COMActions.putScheduledOffersFailure({
                error: error?.message || 'Failed to update scheduled offers'
              })
            );
          })
        );
      })
    )
  );

  // initiates a bulk import for baselines or offers
  initiateBulkImport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.initiateBulkImport),
      exhaustMap(({ registrationId, importName, importType }) => {
        const isCleared = this.router.url.includes('cleared');
        const init$ =
          importType === BulkImportType.Baselines
            ? this.bulkImportService.postBaselinesBulkImport(registrationId, importName)
            : isCleared
              ? this.bulkImportService.postClearedOffersBulkImport(registrationId, importName)
              : this.bulkImportService.postOffersBulkImport(registrationId, importName);
        return init$.pipe(
          map((initResponse) => COMActions.bulkImportInitSuccess({ initResponse })),
          catchError((error) =>
            of(
              COMActions.bulkImportFailed({
                // error: error?.error?.message ? `${error.error.message}: ${error.error.details}` : error?.message || 'Failed to initiate bulk import' // can be used to show detailed error from the api
                error: 'COM.DIALOGS.BULK_UPLOAD.STATUS.IMPORT_INIT_FAILED'
              })
            )
          )
        );
      })
    )
  );

  // uploads the file after bulk import initialization succeeds
  uploadBulkImportFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.bulkImportInitSuccess),
      exhaustMap(({ initResponse }) => {
        const upload$ = this.bulkUploadFileService.upload(initResponse);
        if (!upload$) {
          return of(COMActions.bulkImportFailed({ error: 'COM.DIALOGS.BULK_UPLOAD.STATUS.IMPORT_NO_FILE' }));
        }
        return upload$.pipe(
          map((event) => {
            if (event.type === HttpEventType.UploadProgress && event.total) {
              const progress = Math.round((event.loaded / event.total) * 100);
              return COMActions.bulkImportUploadProgress({ progress });
            }
            if (event.type === HttpEventType.Response) {
              return COMActions.bulkImportUploadComplete();
            }
            return null;
          }),
          filter(
            (action): action is ReturnType<typeof COMActions.bulkImportUploadProgress> | ReturnType<typeof COMActions.bulkImportUploadComplete> =>
              action !== null
          ),
          catchError(() => of(COMActions.bulkImportFailed({ error: 'COM.DIALOGS.BULK_UPLOAD.STATUS.IMPORT_UPLOAD_FAILED' })))
        );
      })
    )
  );

  // polls the import status until a terminal state is reached or timeout
  pollBulkImportStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.bulkImportUploadComplete),
      withLatestFrom(
        this.store.select(COMSelectors.selectBulkImportImportType),
        this.store.select(COMSelectors.selectBulkImportRegistrationId),
        this.store.select(COMSelectors.selectBulkImportImportId)
      ),
      switchMap(([, importType, registrationId, importId]) => {
        const isCleared = this.router.url.includes('cleared');
        const getStatus = () =>
          importType === BulkImportType.Baselines
            ? this.bulkImportService.getBaselinesBulkImportStatus(registrationId!, importId!)
            : isCleared
              ? this.bulkImportService.getClearedOffersBulkImportStatus(registrationId!, importId!)
              : this.bulkImportService.getOffersBulkImportStatus(registrationId!, importId!);

        // emits a signal every x seconds to call getStatus.
        return timer(BULK_IMPORT_POLL_INITIAL_DELAY_MS, BULK_IMPORT_POLL_INTERVAL_MS).pipe(
          // exhaustMap to call get status, if the next tick arrives while still waiting for the previous response, it skips the call and waits
          exhaustMap(() => getStatus().pipe(catchError(() => EMPTY))),
          // extract the status and invalid rows from the response
          map((response) => {
            const { status, total_rows, rows_processed, rows_invalid } = response.data;
            const totalRows = total_rows ?? 0;
            const rowsProcessed = rows_processed ?? 0;
            // guard division by zero (queued state) and clamp to 100
            const percent = totalRows > 0 ? Math.min(100, Math.round((rowsProcessed / totalRows) * 100)) : 0;
            return {
              status: (status as BulkImportStatus) ?? null,
              rowsInvalid: rows_invalid,
              totalRows,
              rowsProcessed,
              percent
            };
          }),
          filter(
            (
              data
            ): data is {
              status: BulkImportStatus;
              rowsInvalid: number;
              totalRows: number;
              rowsProcessed: number;
              percent: number;
            } => data.status !== null
          ),
          // emit when EITHER the status OR the percent changes — this is what lets progress through
          distinctUntilChanged((a, b) => a.status === b.status && a.percent === b.percent),
          takeWhile(({ status }) => !BULK_IMPORT_TERMINAL_STATUSES.has(status), true),
          map(({ status, rowsInvalid, totalRows, rowsProcessed, percent }) =>
            COMActions.bulkImportStatusReceived({
              status,
              importType: importType!,
              rowsInvalid,
              totalRows,
              rowsProcessed,
              percent
            })
          ),
          timeout(BULK_IMPORT_POLL_TIMEOUT_MS),
          catchError(() => of(COMActions.bulkImportFailed({ error: 'COM.DIALOGS.BULK_UPLOAD.STATUS.IMPORT_POLL_TIMEOUT' })))
        );
      })
    )
  );

  // maps terminal import status to completed action to reset state
  handleTerminalImportStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(COMActions.bulkImportStatusReceived),
      filter(({ status }) => BULK_IMPORT_TERMINAL_STATUSES.has(status)),
      map(({ status }) => COMActions.bulkImportCompleted({ status }))
    )
  );

  // shows a toast notification for each bulk import status change
  notifyBulkImportStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(COMActions.bulkImportStatusReceived),
        withLatestFrom(this.store.select(COMSelectors.selectBulkImportImportType)),
        tap(([{ status, rowsInvalid }, importType]) => this.bulkImportNotificationService.showStatusToast(status, importType, rowsInvalid))
      ),
    { dispatch: false }
  );

  // shows an error toast when bulk import fails at any stage
  notifyBulkImportFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(COMActions.bulkImportFailed),
        withLatestFrom(this.store.select(COMSelectors.selectBulkImportImportType)),
        tap(([{ error }, importType]) => this.bulkImportNotificationService.showErrorToast(error, importType))
      ),
    { dispatch: false }
  );

  // fetches invalid record details when import ends with failed or partial status
  fetchInvalidRecordsOnFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(COMActions.bulkImportStatusReceived),
        // only trigger for terminal statuses that may have invalid rows
        filter(({ status }) => status === BulkImportStatus.Failed || status === BulkImportStatus.Partial),
        withLatestFrom(
          this.store.select(COMSelectors.selectBulkImportImportType),
          this.store.select(COMSelectors.selectBulkImportRegistrationId),
          this.store.select(COMSelectors.selectBulkImportImportId)
        ),
        switchMap(([, importType, registrationId, importId]) => {
          const isCleared = this.router.url.includes('cleared');
          // call the appropriate endpoint based on import type to get the invalid rows info
          const getInvalid$ =
            importType === BulkImportType.Baselines
              ? this.bulkImportService.getBaselinesBulkImportInvalid(registrationId!, importId!)
              : isCleared
                ? this.bulkImportService.getClearedOffersBulkImportInvalid(registrationId!, importId!)
                : this.bulkImportService.getOffersBulkImportInvalid(registrationId!, importId!);

          return getInvalid$.pipe(
            tap((response) => {
              const errors = response?.errors ?? [];
              if (errors.length > 0) {
                // uncomment to show error details in a toast
                // this.bulkImportNotificationService.showInvalidRecordsToast(errors);
                console.error('Bulk Import Invalid Records:', errors);
              }
            }),
            // silently ignore fetch errors so the main import flow is unaffected
            catchError(() => EMPTY)
          );
        })
      ),
    { dispatch: false }
  );

  // Open Offers POST or PUT Effects can be added here

  /**
   * Transform API response to GraphDataResponse format
   * Separates the flat array of data points into separate series by metric type
   */
  private transformApiResponseToGraphDataResponse(apiResponse: GraphDataAPIResponse, regConfig: RegConfig): GraphDataResponse {
    if (!apiResponse.timeSeriesData || apiResponse.timeSeriesData.length === 0) {
      return { timeSeriesData: [], metadata: apiResponse.metadata };
    }

    const offerDimensions: GraphDimension[] = regConfig.graph_config?.offer_dimensions ?? ['kW', 'PRICE', 'DEMAND_FORECAST'];

    const basePoint = (point: GraphDataAPIResponse['timeSeriesData'][number]) => ({
      start_dttm_utc: point.start_dttm_utc,
      end_dttm_utc: point.end_dttm_utc,
      dst_editable: point.dst_editable,
      is_holiday: point.is_holiday,
      gate_closed: point.gate_closed,
      opt_out: point.offer_opt_out_state ?? true
    });

    const timeSeriesData: GraphTimeSeriesData[] = offerDimensions
      .filter((dim: GraphDimension) => !!this.DIMENSION_MAP[dim])
      .map((dim: GraphDimension) => {
        const { name, valueKey } = this.DIMENSION_MAP[dim];

        return {
          name,
          id: valueKey,
          dataPoints: apiResponse.timeSeriesData.map((point: GraphDataAPIPoint) => {
            const raw = point.value?.[valueKey as keyof typeof point.value];
            return {
              ...basePoint(point),
              value: raw === null || raw === undefined ? this.DIMENSION_MAP[dim].nullFallback : Number(raw),
              offer_opt_out_state: point.offer_opt_out_state
            };
          })
        };
      });

    return { timeSeriesData, metadata: apiResponse.metadata };
  }

  private getLocalisedLabel(displayLabelMap: { [locale: string]: string }, apiLocale: string, fallbackLocale: string): string {
    if (displayLabelMap[apiLocale] !== undefined) {
      return displayLabelMap[apiLocale];
    } else if (displayLabelMap[fallbackLocale] !== undefined) {
      return displayLabelMap[fallbackLocale];
    } else if (displayLabelMap[Object.keys(displayLabelMap)[0]] !== undefined) {
      return displayLabelMap[Object.keys(displayLabelMap)[0]];
    } else {
      return 'Undefined';
    }
  }

  private mergeBaselineIntoGraph(
    graphOffers: GraphDataResponse,
    baselines: BaselineResponse[],
    startDateUtc: string,
    endDateUtc: string
  ): GraphDataResponse {
    const from = moment.utc(startDateUtc).valueOf();
    const to = moment.utc(endDateUtc).valueOf();

    const dataPoints: GraphDataPoint[] = this.baselineToGraphPoints(baselines)
      .filter((p: GraphDataPoint) => {
        const t = moment.utc(p.start_dttm_utc).valueOf();
        return t >= from && t <= to;
      })
      .sort((a, b) => moment.utc(a.start_dttm_utc).valueOf() - moment.utc(b.start_dttm_utc).valueOf());

    if (!dataPoints.length) return graphOffers;

    const baselineSeries: GraphTimeSeriesData = {
      id: 'BASELINE',
      name: 'Baseline',
      dataPoints
    } as GraphTimeSeriesData;

    return { ...graphOffers, timeSeriesData: [...graphOffers.timeSeriesData, baselineSeries] };
  }

  private baselineToGraphPoints(baselines: BaselineResponse[]): GraphDataPoint[] {
    return baselines.map((b: BaselineResponse): GraphDataPoint => ({
      start_dttm_utc: b.baseline_start_dttm_utc,
      end_dttm_utc: b.baseline_end_dttm_utc,
      value: b.baseline_value ?? null,
      offer_opt_out_state: false,
      dst_editable: b.dst_editable,
      gate_closed: false
    }));
  }
}
